import { useOrganizationsControllerGet } from '@batiflow/api-client';
import { EmptyState, SkeletonList } from '@batiflow/ui';
import {
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import { Building24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { SpaceDocumentsTab, useSpace } from '@/features/spaces';

/**
 * The organisation's own documents (GED-FE-09): its space in the folder workspace of the projects.
 * What each folder shows is decided by the API (the expense notes and the vault only for their
 * readers); creating a folder at the root needs `can_manage` on the space.
 */
export function OrganisationFiles({ orgId }: { orgId: string }) {
  const organization = useOrganizationsControllerGet(orgId);
  const spaceId = organization.data?.spaceId;
  if (organization.isError) return <LoadFailed retry={() => void organization.refetch()} />;
  if (organization.isPending) return <SkeletonList count={5} />;
  if (!spaceId) return <NoSpace />;
  return <OrganisationSpace orgId={orgId} spaceId={spaceId} />;
}

function OrganisationSpace({ orgId, spaceId }: { orgId: string; spaceId: string }) {
  const space = useSpace(spaceId);
  if (space.isError) return <LoadFailed retry={() => void space.refetch()} />;
  if (space.isPending) return <SkeletonList count={5} />;
  return <SpaceDocumentsTab space={space.data} readerOrgId={orgId} />;
}

function LoadFailed({ retry }: { retry: () => void }) {
  const { t } = useTranslation('documents');
  const { t: tCommon } = useTranslation('common');
  return (
    <MessageBar intent="error" layout="multiline">
      <MessageBarBody>
        <MessageBarTitle>{t('filesApp.organisation.loadFailedTitle')}</MessageBarTitle>
        {t('filesApp.organisation.loadFailedBody')}
      </MessageBarBody>
      <MessageBarActions>
        <Button onClick={retry}>{tCommon('actions.retry')}</Button>
      </MessageBarActions>
    </MessageBar>
  );
}

function NoSpace() {
  const { t } = useTranslation('documents');
  return (
    <EmptyState
      illustration={<Building24Regular />}
      title={t('filesApp.organisation.noSpaceTitle')}
      description={t('filesApp.organisation.noSpaceBody')}
    />
  );
}
